import React, { useState, useEffect } from 'react';
import RenterRequirementsBanner from './RenterRequirementsBanner';
import { auth, db } from '../firebase';
import { RELEASE, buildStamp, isDeployedBuild } from '../version';
import ChangelogDialog from './ChangelogDialog';
import { doc, getDoc } from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';
import { resolveMember } from '../utils/resolveMember';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton,
  List, ListItem, ListItemIcon, ListItemText,
  Avatar, Menu, MenuItem, useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import PaymentIcon from '@mui/icons-material/Payment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BarChartIcon from '@mui/icons-material/BarChart';
import UploadIcon from '@mui/icons-material/Upload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PaletteIcon from '@mui/icons-material/Palette';
import SchoolIcon from '@mui/icons-material/School';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { getMenuIcon, DEFAULT_MENU_ITEMS } from '../config/menuIcons';

const DRAWER_WIDTH = 200;

const ADMIN_SUB_PATHS = [
  '/admin',
  '/admin/dashboard-tiles',
  '/admin/menu-items',
  '/plans',
  '/reports',
  '/import',
  '/notifications',
  '/admin/users',
  '/settings',
  '/tournament-admin',
  '/admin/branding',
  '/admin/court-rentals',
];

function Layout({ children, bg }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  // Initialize from sessionStorage to avoid a one-frame flash where roles=[] would
  // briefly render the parent/athlete nav (which has "My Account" at position #2)
  // every time Layout remounts on route change.
  const [roles, setRoles] = useState(() => {
    try {
      const cached = sessionStorage.getItem('tbv_layout_roles');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [displayName, setDisplayName] = useState(() => sessionStorage.getItem('tbv_layout_displayName') || '');
  // Court renters have two things to satisfy before they can book. Read from
  // the user doc this component already fetches, so telling them costs no
  // extra round trip.
  const [renter, setRenter] = useState({ isRenter: false, insured: true });
  // Menu items loaded from Firestore (or seeded from defaults).
  // Cached in sessionStorage like roles to avoid flicker on Layout remount.
  const [menuItems, setMenuItems] = useState(() => {
    try {
      const cached = sessionStorage.getItem('tbv_layout_menu_items');
      return cached ? JSON.parse(cached) : DEFAULT_MENU_ITEMS;
    } catch { return DEFAULT_MENU_ITEMS; }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const branding = useBranding();
  const user = auth.currentUser;



  useEffect(() => {
    const fetchRoles = async (u) => {
      if (!u) return;
      try {
        // Through resolveMember, not straight at users/{uid}: a login that was
        // merged into another account keeps only a pointer on its own doc, with
        // the roles, the name and the membership all on the canonical one.
        // Reading the alias directly returns no roles, and since every menu item
        // requires at least one, that renders an empty menu — the member signs
        // in successfully and lands on a page with nothing to navigate to.
        const { data: d } = await resolveMember(db, u);
        if (d) {
          const nextRoles = d.roles || [];
          setRoles(nextRoles);
          try { sessionStorage.setItem('tbv_layout_roles', JSON.stringify(nextRoles)); } catch {}
          const name = d.firstName && d.lastName ? `${d.firstName} ${d.lastName}` : d.firstName || '';
          setDisplayName(name);
          try { sessionStorage.setItem('tbv_layout_displayName', name); } catch {}
          setRenter({
            isRenter: (d.membership || {}).pricingMode === 'court_rental',
            insured: d.liabilityInsuranceConfirmed === true,
          });
        }
      } catch (e) { console.error('Layout fetchRoles error:', e); }
    };

    // Try immediately if already signed in
    const u = auth.currentUser;
    if (u) fetchRoles(u);

    // Also listen for auth state changes in case not yet resolved.
    // On sign-out, clear the cache so a different user doesn't briefly see prior roles.
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchRoles(user);
      } else {
        try {
          sessionStorage.removeItem('tbv_layout_roles');
          sessionStorage.removeItem('tbv_layout_displayName');
        } catch {}
      }
    });
    return unsub;
  }, []);

  // Load menu items from Firestore (or fall back to defaults).
  // Runs once per Layout mount. Cached in sessionStorage to avoid flicker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'menuItems'));
        if (cancelled) return;
        if (snap.exists() && snap.data().items?.length > 0) {
          const loaded = [...snap.data().items].sort((a, b) => (a.order || 0) - (b.order || 0));
          setMenuItems(loaded);
          try { sessionStorage.setItem('tbv_layout_menu_items', JSON.stringify(loaded)); } catch {}
        }
        // If no Firestore doc exists, we just keep using DEFAULT_MENU_ITEMS
        // (already in state from sessionStorage init).
      } catch (e) {
        console.error('Layout menu items load failed, using defaults:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isAdmin = roles.includes('admin');
  const [changelogOpen, setChangelogOpen] = useState(false);

  const handleSignOut = async () => {
    setAnchorEl(null);
    // Clear cached layout state so the next user (or refresh) doesn't briefly
    // render the previous user's menu/name.
    try {
      sessionStorage.removeItem('tbv_layout_roles');
      sessionStorage.removeItem('tbv_layout_displayName');
      sessionStorage.removeItem('tbv_layout_menu_items');
    } catch {}
    await signOut(auth);
    // Send everyone to the public landing page after sign-out. ?dashboard=1 so
    // it lands on Landing even if React hasn't flushed the auth-state change
    // yet — without it the still-logged-in branch of RootRedirect wins the race
    // and bounces to startPage, which then punts to /login.
    //
    // A full page load rather than navigate(): the other half of the same race
    // is the router still matching the gated route we signed out ON while the
    // user is already null, so that route's redirect to /login fires and wins.
    // 1.2.7 fixed the logged-in side of this; the logged-out side landed on
    // /login?next=%2F%3Fdashboard%3D1 — the next= is this very navigation,
    // caught mid-flight. Leaving the page outright is the only version of this
    // with nothing left in flight to race, and it starts the next visitor on a
    // clean store rather than the previous member's cached React state.
    window.location.assign('/?dashboard=1');
  };

  const handleMyAccount = () => {
    setAnchorEl(null);
    navigate('/my-account');
  };

  const handleMyDocuments = () => {
    setAnchorEl(null);
    navigate('/my-documents');
  };

  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Shared nav item style
  const navItemSx = (active) => ({
    mx: 1, mb: 0.5, borderRadius: 2,
    backgroundColor: active ? `${branding.chromeAccent}33` : 'transparent', // 33 = 20% alpha
    borderLeft: active ? `3px solid ${branding.chromeAccent}` : '3px solid transparent',
    '&:hover': { backgroundColor: branding.chromeHover },
  });

  const navIconSx = (active) => ({
    color: active ? branding.chromeAccent : branding.chromeTextSubtle, minWidth: 40
  });

  const navTextSx = (active) => ({
    '& .MuiListItemText-primary': {
      color: active ? branding.chromeAccent : branding.chromeText,
      fontSize: '0.9rem',
      fontWeight: active ? 600 : 400
    }
  });

  // Sub-items indented style
  const subItemSx = (active) => ({
    mx: 1, mb: 0.5, borderRadius: 2, pl: 2,
    backgroundColor: active ? `${branding.chromeAccent}33` : 'transparent',
    borderLeft: active ? `3px solid ${branding.chromeAccent}` : '3px solid transparent',
    '&:hover': { backgroundColor: branding.chromeHover },
  });

  const adminSubItems = [
    { label: 'Dashboard Tiles',   icon: <DashboardCustomizeIcon />, path: '/admin/dashboard-tiles' },
    { label: 'Menu Items',        icon: <MenuBookIcon />,           path: '/admin/menu-items' },
    { label: 'Plans',             icon: <PaymentIcon />,            path: '/plans' },
    { label: 'Reports',           icon: <BarChartIcon />,           path: '/reports' },
    { label: 'Import',            icon: <UploadIcon />,             path: '/import' },
    { label: 'Notifications',     icon: <NotificationsIcon />,      path: '/notifications' },
    // { label: 'Manage Users',      icon: <ManageAccountsIcon />,     path: '/admin/users' },     // hidden
    // { label: 'Settings',          icon: <SettingsIcon />,           path: '/settings' },        // hidden
    { label: 'Tournament Admin',  icon: <EmojiEventsIcon />,        path: '/tournament-admin' },
    { label: 'Branding',          icon: <PaletteIcon />,            path: '/admin/branding' },
	{ label: 'Schools',         icon: <SchoolIcon />,        path: '/admin/schools' },
    { label: 'Court Rentals',   icon: <SportsVolleyballIcon />, path: '/admin/court-rentals' },
  ];

  const isAdminSubActive = ADMIN_SUB_PATHS.some(p => location.pathname.startsWith(p));

  // Top nav is now driven entirely by the menuItems collection in Firestore.
  // For each item, show it if:
  //   - it's enabled
  //   - the user has at least one of the roles listed in item.roles
  // Items are already pre-sorted by `order` when loaded.
  const topNavItems = menuItems
    .filter(item => item.enabled !== false)
    .filter(item => Array.isArray(item.roles) && item.roles.some(r => roles.includes(r)))
    .map(item => ({
      label: item.label,
      path: item.path,
      icon: React.createElement(getMenuIcon(item.icon)),
    }));

  const drawerContent = (
    <Box sx={{ height: '100vh', backgroundColor: branding.chromeBg, color: 'white', display: 'flex', flexDirection: 'column' }}>

      {/* Logo & Club Name */}
      <Box sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
        <img src={branding.logoUrl || '/torch_logo.png'} alt={branding.clubName} style={{ width: 60, height: 60, objectFit: 'contain' }} />
        <Typography variant="subtitle2" sx={{ color: branding.chromeAccent, fontWeight: 'bold', letterSpacing: 1, mt: 0.5 }}>
          {(branding.clubName || 'TORCH BEACH VB').toUpperCase()}
        </Typography>
      </Box>

      {/* Scrollable Nav */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        <List sx={{ pt: 1 }}>

          {/* Top-level items */}
          {topNavItems.map((item) => {
            const isActive = location.pathname === item.path || location.search === '?dashboard=1' && item.path === '/?dashboard=1';
            return (
              <ListItem
                button
                key={item.label}
                onClick={() => handleNav(item.path)}
                sx={navItemSx(isActive)}
              >
                <ListItemIcon sx={navIconSx(isActive)}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} sx={navTextSx(isActive)} />
              </ListItem>
            );
          })}

          {/* Administration — single nav item. Admins only: court_admin is a
              calendar role (names on bookings, moving them between courts on
              Schedule) and deliberately carries no admin rights. */}
          {isAdmin && (() => {
            const isActive = isAdminSubActive || location.pathname === '/admin';
            return (
              <ListItem button onClick={() => handleNav('/admin')} sx={navItemSx(isActive)}>
                <ListItemIcon sx={navIconSx(isActive)}><AdminPanelSettingsIcon /></ListItemIcon>
                <ListItemText primary="Administration" sx={navTextSx(isActive)} />
              </ListItem>
            );
          })()}

        </List>
      </Box>

      {/* Profile Bar */}
      <Box sx={{
        flexShrink: 0,
        borderTop: '1px solid rgba(255,255,255,0.1)',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}>
        <Avatar sx={{ width: 36, height: 36, backgroundColor: branding.chromeAccent, fontSize: '0.9rem', flexShrink: 0 }}>
          {(displayName || user?.displayName || user?.email)?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {displayName || user?.displayName || user?.email?.split('@')[0] || 'User'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
            {user?.email}
          </Typography>
        </Box>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: 'rgba(255,255,255,0.7)', flexShrink: 0 }}>
          <MoreVertIcon />
        </IconButton>
      </Box>

      {/* Version. Everyone sees which version of the app they are on — when
          someone reports something odd, that is the first useful question and
          they can only answer it if they can see it.

          Admins get the rest: the build date and commit, which identify one
          deploy out of the many that share a release, and a tap opens what
          changed. Members have no use for a commit hash. */}
      <Box sx={{ flexShrink: 0, px: 1.5, pb: 1, textAlign: 'center' }}>
        <Typography
          variant="caption"
          onClick={isAdmin ? () => setChangelogOpen(true) : undefined}
          title={isAdmin
            ? (isDeployedBuild() ? 'Release · build date · commit — tap for changes' : 'Local development build')
            : undefined}
          sx={{
            color: 'rgba(255,255,255,0.22)', fontSize: '0.62rem', letterSpacing: '0.5px',
            userSelect: 'text',
            ...(isAdmin ? { cursor: 'pointer', '&:hover': { color: 'rgba(201,168,76,0.6)' } } : {}),
          }}
        >
          {isAdmin ? buildStamp() : `v${RELEASE}`}
        </Typography>
      </Box>
      {isAdmin && <ChangelogDialog open={changelogOpen} onClose={() => setChangelogOpen(false)} />}

      {/* Kebab Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem onClick={handleMyAccount}>My Account</MenuItem>
        <MenuItem onClick={handleMyDocuments}>My Documents</MenuItem>
        <MenuItem onClick={handleSignOut}>Logout</MenuItem>
      </Menu>

    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', overflowX: 'hidden', backgroundColor: bg || branding.pageBg }}>

      {/* Top App Bar - mobile only */}
      <AppBar position="fixed" sx={{ display: { md: 'none' }, backgroundColor: branding.chromeBg, zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
            <img src={branding.logoUrl || '/torch_logo.png'} alt={branding.clubName} style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                letterSpacing: 1,
                color: branding.chromeAccent,
                lineHeight: 1.05,
                whiteSpace: 'normal',
                overflow: 'hidden',
                fontSize: '0.75rem',
              }}
            >
              {(branding.clubName || 'Torch Beach Volleyball').toUpperCase()}
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, backgroundColor: branding.chromeAccent, fontSize: '0.9rem' }}>
              {(displayName || user?.displayName || user?.email)?.charAt(0)?.toUpperCase()}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Sidebar - desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          backgroundColor: branding.chromeBg,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none', overflow: 'hidden', backgroundColor: branding.chromeBg },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Sidebar - mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', overflow: 'hidden' },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main content */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: 'hidden', backgroundColor: bg || branding.pageBg, minHeight: '100vh', pt: { xs: 7, md: 0 } }}>
        <RenterRequirementsBanner isRenter={renter.isRenter} insured={renter.insured} />
        {children}
      </Box>

    </Box>
  );
}

export default Layout;
